/* eslint-disable no-restricted-properties */

enum ShopifyPageType {
  Home = 'home',
  Product = 'product',
  Collection = 'collection',
  Collections = 'collections',
  Vendors = 'vendors',
  Page = 'page',
  Cart = 'cart',
}
export function getPageType(): ShopifyPageType | undefined {
  const convermaxPage = window.Convermax.Shopify?.pageType;
  if (convermaxPage) {
    switch (convermaxPage) {
      case 'index':
        return ShopifyPageType.Home;
      case 'collection':
        return window.Convermax.Shopify?.collectionVendor
          ? // vendor collection page (/collections/vendors?q=)
            ShopifyPageType.Vendors
          : ShopifyPageType.Collection;
      case 'list-collections':
        return ShopifyPageType.Collections;
      default:
        return convermaxPage as ShopifyPageType;
    }
  }

  return window.ShopifyAnalytics?.meta?.page?.pageType as ShopifyPageType | undefined;
}

export function getProductId(): number | undefined {
  return window.Convermax.Shopify?.productId ?? window.ShopifyAnalytics?.meta?.product?.id;
}
export function getProductType(): string | undefined {
  return window.Convermax.Shopify?.productType ?? window.ShopifyAnalytics?.meta?.product?.type;
}
export function getVariantId(): number | undefined {
  // always get the updated variant id on a variant selection
  const urlVariantId = new URLSearchParams(window.location.search).get('variant');
  return urlVariantId
    ? +urlVariantId
    : (window.Convermax.Shopify?.selectedVariantId ??
        window.ShopifyAnalytics?.meta?.product?.variants?.[0].id);
}

export function getCollectionId(): number | undefined {
  return (
    window.Convermax.Shopify?.collectionId ??
    (getPageType() === ShopifyPageType.Collection
      ? window.ShopifyAnalytics?.meta?.page?.resourceId
      : undefined)
  );
}
export function getPageId(): number | undefined {
  return (
    window.Convermax.Shopify?.pageId ??
    (getPageType() === ShopifyPageType.Page ? window.ShopifyAnalytics?.meta?.page?.resourceId : undefined)
  );
}

export function getCustomerId(): number | undefined {
  return (
    window.Convermax.Shopify?.customerId ??
    window.ShopifyAnalytics?.meta?.page?.customerId ??
    window.meta?.page?.customerId ??
    window.__st?.cid
  );
}
export function getCompanyId(): number | undefined {
  return window.Convermax.Shopify?.companyId;
}
export function getCompanyLocationId(): number | undefined {
  return window.Convermax.Shopify?.companyLocationId;
}

export function getCountry(): string | undefined {
  return window.Convermax.Shopify?.country ?? window.Shopify?.country;
}

export function getLocale(): string | undefined {
  return window.Convermax.Shopify?.locale ?? window.Shopify?.locale?.split('-')?.[0];
}

export function getCurrency(): string | undefined {
  return window.Convermax.Shopify?.currency ?? window.Shopify?.currency?.active;
}
export function getCurrencyRate(): string | undefined {
  // there is no way to obtain a rate using liquid
  return window.Shopify?.currency?.rate;
}

export function getRootRoute(): string {
  return (window.Convermax.Shopify?.rootRoute ?? window.Shopify?.routes?.root ?? '').replace(/\/+$/, '');
}

type CartUrls = {
  root?: string;
  add?: string;
  change?: string;
  clear?: string;
  update?: string;
};
export function getCartUrls(): CartUrls {
  const cmShopify = window.Convermax.Shopify;
  const rootRoute = getRootRoute();

  return {
    root: cmShopify?.cartUrl ?? `${rootRoute}/cart`,
    add: cmShopify?.cartAddUrl ?? `${rootRoute}/cart/add`,
    change: cmShopify?.cartChangeUrl ?? `${rootRoute}/cart/change`,
    clear: cmShopify?.cartClearUrl ?? `${rootRoute}/cart/clear`,
    update: cmShopify?.cartUpdateUrl ?? `${rootRoute}/cart/update`,
  };
}

export function getSearchPageUrl(): string {
  return window.Convermax.Shopify?.searchPageUrl ?? `${getRootRoute()}/pages/search`;
}
export function getCollectionsPageUrl(): string {
  return window.Convermax.Shopify?.collectionsUrl ?? `${getRootRoute()}/collections`;
}

type ShopPayTerms = {
  min_price: string;
  max_price: string;
  financing_plans: [
    {
      min_price: string;
      max_price: string;
      terms: [{ apr: number; loan_type: string; installments_count: number }];
    },
    {
      min_price: string;
      max_price: string;
      terms: [{ apr: number; loan_type: string; installments_count: number }];
    },
    {
      min_price: string;
      max_price: string;
      terms: [
        { apr: number; loan_type: string; installments_count: number },
        { apr: number; loan_type: string; installments_count: number },
        { apr: number; loan_type: string; installments_count: number },
        { apr: number; loan_type: string; installments_count: number },
      ];
    },
    {
      min_price: string;
      max_price: string;
      terms: [
        { apr: number; loan_type: string; installments_count: number },
        { apr: number; loan_type: string; installments_count: number },
        { apr: number; loan_type: string; installments_count: number },
      ];
    },
  ];
  installments_buyer_prequalification_enabled: boolean;
  seller_id: number;
};
export function getShopPayText(price: number, terms?: ShopPayTerms): string | undefined {
  // You must reside within the United States (U.S.) and use a payment card with a United States billing address. All transactions must be carried out in USD.
  // https://help.shop.app/hc/en-us/articles/4408790562836-Shop-Pay-Installments-eligibility-and-requirements#:~:text=1.%20Eligibility%20criteria

  const country = getCurrency();
  const currency = getCountry();
  const shopPayTerms = terms ?? window.Convermax.Shopify?.shopPayTerms;

  if (!shopPayTerms || country !== 'US' || currency !== 'USD') {
    return undefined;
  }

  const toNumber = (price: string) => Number(price.replace(',', '').replace('$', ''));

  if (price < toNumber(shopPayTerms.min_price)) {
    return `Pay over time for orders over <b>${shopPayTerms.min_price}</b> with`;
  } else if (price > toNumber(shopPayTerms.max_price)) {
    return `Split your purchase into installments for orders up to <b>${shopPayTerms.max_price}</b> with`;
  }

  const eligiblePlans = shopPayTerms.financing_plans.filter(
    (plan) => price >= toNumber(plan.min_price) && price <= toNumber(plan.max_price),
  );

  const splitPayTerm = eligiblePlans
    .find((plan) => plan.terms.some((term) => term.loan_type === 'split_pay'))
    ?.terms.find((term) => term.loan_type === 'split_pay');

  const interestTerms = eligiblePlans
    .find((plan) => plan.terms.some((term) => term.loan_type === 'interest'))
    ?.terms.filter((term) => term.loan_type === 'interest');

  const longestInterestTerm = interestTerms?.length
    ? interestTerms.reduce(
        (max, term) => (term.installments_count > max.installments_count ? term : max),
        interestTerms[0],
      )
    : null;

  let monthlyPaymentFormat = '';
  const priceFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

  if (longestInterestTerm) {
    const interestRate = longestInterestTerm.apr / 100;
    const paymentsCount = longestInterestTerm.installments_count;

    const monthlyPayment = (price * (interestRate / 12)) / (1 - (1 + interestRate / 12) ** -paymentsCount);
    monthlyPaymentFormat = priceFormatter.format(monthlyPayment);
  }

  if (splitPayTerm && longestInterestTerm) {
    return `${splitPayTerm.installments_count} interest-free installments, or from <b>${monthlyPaymentFormat}</b>/mo with`;
  } else if (longestInterestTerm) {
    return `From <b>${monthlyPaymentFormat}</b>/mo with`;
  } else if (splitPayTerm) {
    monthlyPaymentFormat = priceFormatter.format(price / splitPayTerm.installments_count);
    return `Pay in ${splitPayTerm.installments_count} interest-free installments of <b>${monthlyPaymentFormat}</b> with`;
  }
}

export type ConvermaxShopifyGlobal = {
  cartUrl?: string;
  cartAddUrl?: string;
  cartChangeUrl?: string;
  cartClearUrl?: string;
  cartUpdateUrl?: string;
  collectionId?: number;
  collectionsUrl?: string;
  collectionVendor?: string;
  country?: string;
  currency?: string;
  currencyRate?: string;
  customerId?: number;
  companyId?: number;
  companyLocationId?: number;
  locale?: string;
  pageType?: string;
  pageId?: number;
  productId?: number;
  productType?: string;
  rootRoute?: string;
  searchPageUrl?: string;
  selectedVariantId?: number;
  shopPayTerms?: ShopPayTerms;
} & ConvermaxShopifyHelpers;
type ConvermaxShopifyHelpers = {
  getCartUrls: () => CartUrls;
  getCollectionId: () => number | undefined;
  getCollectionsPageUrl: () => string;
  getCountry: () => string | undefined;
  getCurrency: () => string | undefined;
  getCustomerId: () => number | undefined;
  getCompanyId: () => number | undefined;
  getCompanyLocationId: () => number | undefined;
  getLocale: () => string | undefined;
  getPageId: () => number | undefined;
  getPageType: () => ShopifyPageType | undefined;
  getProductId: () => number | undefined;
  getProductType: () => string | undefined;
  getRootRoute: () => string | undefined;
  getSearchPageUrl: () => string;
  getShopPayText: (price: number, terms?: ShopPayTerms) => string | undefined;
  getVariantId: () => number | undefined;
};

export type Shopify = {
  country?: string;
  currency?: {
    active?: string;
    rate?: string;
  };
  locale?: string;
  routes?: {
    root?: string;
  };
  shop?: string;
  theme?: {
    id?: number;
    name?: string;
    schema_name?: string;
    schema_version?: string;
    role?: string;
  };
};
export type ShopifyAnalytics = {
  meta?: {
    page?: {
      pageType?: string;
      resourceId?: number;
      customerId?: number;
    };
    product?: {
      id?: number;
      type?: string;
      variants?: [
        {
          id?: number;
        },
      ];
    };
  };
};
export type ShopifyMeta = {
  page?: {
    customerId?: number;
  };
};
export type ShopifyTools = {
  cid?: number;
};

const shopifyHelpers: ConvermaxShopifyHelpers = {
  getCartUrls,
  getCollectionsPageUrl,
  getCollectionId,
  getCountry,
  getCurrency,
  getCustomerId,
  getCompanyId,
  getCompanyLocationId,
  getLocale,
  getPageId,
  getPageType,
  getProductId,
  getProductType,
  getRootRoute,
  getSearchPageUrl,
  getShopPayText,
  getVariantId,
};
export default shopifyHelpers;
